import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from '../../services/api';

/**
 * Admin Broadcast — compose an announcement subject + content and send to
 * every user in the database (with respect for user opt-out preferences by
 * default). Test mode routes to a single specified address instead, using
 * the exact same email pipeline so admin can verify formatting first.
 *
 * Variables supported in both subject and content: {firstName}, {username},
 * {email}. Rendered per recipient.
 */

const STORAGE_KEY = 'admin-broadcast-draft';

interface Draft {
  subject: string;
  content: string;
  testMode: boolean;
  testEmail: string;
  respectPreferences: boolean;
}

const EMPTY_DRAFT: Draft = {
  subject: '',
  content: '',
  testMode: true, // safe default — sends to one address until admin uncheckss
  testEmail: '',
  respectPreferences: true,
};

export function AdminBroadcast() {
  const { getAccessTokenSilently, user } = useAuth0();

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [audience, setAudience] = useState<{ total: number; eligible: number } | null>(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    sent?: number;
    failed?: number;
    skipped?: number;
    errors?: string[];
  } | null>(null);

  // Load any saved draft on mount + prefill test email with admin's address
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<Draft>;
        setDraft((prev) => ({ ...prev, ...parsed }));
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (user?.email && !draft.testEmail) {
      setDraft((prev) => ({ ...prev, testEmail: user.email || '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Persist draft to localStorage on change
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch {
      // ignore
    }
  }, [draft]);

  // Refresh audience count when respectPreferences flips
  useEffect(() => {
    if (draft.testMode) return; // not needed in test mode
    let cancelled = false;
    async function load() {
      try {
        setAudienceLoading(true);
        const token = await getAccessTokenSilently();
        const data = await api.getBroadcastAudience(draft.respectPreferences, token);
        if (!cancelled) setAudience({ total: data.total, eligible: data.eligible });
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setAudienceLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [draft.testMode, draft.respectPreferences, getAccessTokenSilently]);

  const validSubject = draft.subject.trim().length > 0;
  const validContent = draft.content.trim().length > 0;
  const validTestEmail = !draft.testMode || draft.testEmail.trim().length > 3;
  const canSubmit = validSubject && validContent && validTestEmail && !sending;

  // Live preview that matches what the backend renders:
  //   1. Substitute {firstName} / {username} / {email}
  //   2. If content is plain text (no HTML tags), convert blank-line gaps to
  //      paragraphs and single newlines to <br>. Then linkify URLs.
  //   3. If content already contains HTML, just linkify URLs.
  // Mirrors backend's formatBroadcastContent() so the preview is truthful.
  const preview = useMemo(() => {
    const sample = {
      firstName: 'Alex',
      username: 'alex-pp',
      email: 'alex@example.com',
    };
    const substitute = (s: string) =>
      s
        .replace(/\{firstName\}/g, sample.firstName)
        .replace(/\{username\}/g, sample.username)
        .replace(/\{email\}/g, sample.email);

    const looksLikeHtml = (s: string) => /<\s*[a-zA-Z\/]/.test(s);
    const escapeHtml = (s: string) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const linkify = (html: string) =>
      html.replace(
        /(^|[^"'>=])(https?:\/\/[^\s<>"']+)/g,
        (_m, lead, url) =>
          `${lead}<a href="${url}" style="color:#2563eb;text-decoration:underline;">${url}</a>`,
      );

    const formatContent = (raw: string) => {
      const subbed = substitute(raw);
      if (looksLikeHtml(subbed)) return linkify(subbed);
      const escaped = escapeHtml(subbed.trim());
      const paragraphs = escaped
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean);
      const html = paragraphs
        .map((p) => `<p style="margin:0 0 14px 0">${p.replace(/\n/g, '<br>')}</p>`)
        .join('\n');
      return linkify(html);
    };

    return {
      subject: substitute(draft.subject),
      content: formatContent(draft.content),
    };
  }, [draft.subject, draft.content]);

  async function performSend() {
    setShowConfirm(false);
    setSending(true);
    setResult(null);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.sendBroadcast(
        {
          subject: draft.subject,
          content: draft.content,
          testMode: draft.testMode,
          testEmail: draft.testEmail.trim() || undefined,
          respectPreferences: draft.respectPreferences,
        },
        token,
      );
      setResult(res);
    } catch (err: any) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Send failed',
      });
    } finally {
      setSending(false);
    }
  }

  function handleSendClick() {
    if (!canSubmit) return;
    // Test mode skips the confirm modal — sends immediately to one address
    if (draft.testMode) {
      performSend();
    } else {
      setShowConfirm(true);
    }
  }

  function clearDraft() {
    setDraft({ ...EMPTY_DRAFT, testEmail: user?.email || '' });
    setResult(null);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <Link to="/admin" className="text-blue-600 hover:underline text-sm">
          ← Back to Admin
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">Broadcast Email</h1>
        <p className="text-gray-600 mt-1">
          Compose an announcement and send it to every user in the database. Test mode sends to a
          single address first so you can verify before broadcasting.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compose form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Subject */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              placeholder="A new pool just launched, {firstName}!"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={sending}
            />
          </div>

          {/* Content */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Plain text or HTML. Blank lines become paragraph breaks, single newlines become
              line breaks, and URLs are auto-linked. The live preview shows exactly what the
              email will look like.
            </p>
            <textarea
              value={draft.content}
              onChange={(e) => setDraft({ ...draft, content: e.target.value })}
              placeholder={`<p>Hi {firstName},</p>\n<p>The 2026 World Cup pools are now open. Click below to join…</p>\n<p><a href="https://propoolpicks.com/pools">View pools</a></p>`}
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
              disabled={sending}
            />
          </div>

          {/* Test mode + recipient */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.testMode}
                onChange={(e) => setDraft({ ...draft, testMode: e.target.checked })}
                disabled={sending}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="font-medium text-gray-900">Test mode</span>
              <span className="text-sm text-gray-500">
                — send to one address only (recommended for verification)
              </span>
            </label>
            {draft.testMode && (
              <div className="mt-3 pl-6">
                <label className="block text-xs font-medium text-gray-600 mb-1">Test recipient</label>
                <input
                  type="email"
                  value={draft.testEmail}
                  onChange={(e) => setDraft({ ...draft, testEmail: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={sending}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Same Brevo pipeline as the real broadcast. Subject is prefixed with [TEST].
                </p>
              </div>
            )}
          </div>

          {/* Live mode options */}
          {!draft.testMode && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.respectPreferences}
                  onChange={(e) => setDraft({ ...draft, respectPreferences: e.target.checked })}
                  disabled={sending}
                  className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span>
                  <span className="font-medium text-amber-900">Respect user preferences</span>
                  <span className="block text-sm text-amber-800">
                    When checked, users who opted out of announcements in Profile won't get this
                    email. Uncheck only for essential / emergency communications.
                  </span>
                </span>
              </label>
              <div className="mt-3 pt-3 border-t border-amber-200 text-sm text-amber-900">
                {audienceLoading ? (
                  <span>Counting audience…</span>
                ) : audience ? (
                  <>
                    Will send to <strong>{audience.eligible}</strong> users
                    {draft.respectPreferences && audience.eligible < audience.total && (
                      <span> (of {audience.total} total — {audience.total - audience.eligible} opted out)</span>
                    )}
                    .
                  </>
                ) : (
                  <span>Audience size unavailable.</span>
                )}
              </div>
            </div>
          )}

          {/* Send button */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSendClick}
              disabled={!canSubmit}
              className={`px-6 py-2.5 rounded-lg font-semibold text-white transition-colors ${
                draft.testMode
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {sending
                ? 'Sending…'
                : draft.testMode
                ? `Send Test to ${draft.testEmail.trim() || '…'}`
                : `Send to ${audience?.eligible ?? '?'} Users`}
            </button>
            <button
              type="button"
              onClick={clearDraft}
              disabled={sending}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Clear draft
            </button>
          </div>

          {/* Result banner */}
          {result && (
            <div
              className={`p-4 rounded-lg border ${
                result.success
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                  : 'bg-rose-50 border-rose-300 text-rose-900'
              }`}
              role="status"
            >
              <p className="font-semibold">{result.success ? '✓ Sent' : '✗ Failed'}</p>
              <p className="text-sm mt-1">{result.message}</p>
              {result.errors && result.errors.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer">Errors ({result.errors.length})</summary>
                  <ul className="mt-1 pl-4 list-disc max-h-40 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Sidebar — variables + preview */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Variables</h3>
            <p className="text-xs text-gray-600 mb-3">
              These are replaced per recipient in both subject and content.
            </p>
            <ul className="space-y-1 text-sm font-mono">
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{firstName}'}</code> — first name or username</li>
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{username}'}</code> — username</li>
              <li><code className="bg-gray-100 px-1.5 py-0.5 rounded">{'{email}'}</code> — recipient email</li>
            </ul>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Live preview</h3>
            <p className="text-xs text-gray-500 mb-2">
              Sample recipient: Alex (alex-pp, alex@example.com)
            </p>
            <div className="border border-gray-200 rounded p-3 bg-gray-50">
              <p className="text-xs uppercase tracking-wide text-gray-500">Subject</p>
              <p className="font-medium text-gray-900 mb-3 break-words">
                {preview.subject || <span className="italic text-gray-400">(empty)</span>}
              </p>
              <p className="text-xs uppercase tracking-wide text-gray-500">Content</p>
              <div
                className="prose prose-sm max-w-none text-gray-800 break-words"
                dangerouslySetInnerHTML={{
                  __html: preview.content || '<span style="color:#9ca3af;font-style:italic">(empty)</span>',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Send to every user?</h3>
            <p className="text-gray-600 mb-4">
              About to email <strong>{audience?.eligible ?? '?'}</strong> users
              {!draft.respectPreferences && (
                <span className="text-rose-600"> (ignoring opt-out preferences)</span>
              )}
              . This can't be undone.
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-4 text-sm">
              <p className="font-medium text-gray-800 mb-1">Preview:</p>
              <p className="text-gray-600">
                <span className="font-medium">Subject:</span> {preview.subject}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={performSend}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg"
              >
                Send broadcast
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBroadcast;
